import { AvailableModules, Features } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'

import { locale } from 'modules/marketplace/components/utils/locales'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'

interface Props {
  moduleRequested: AvailableModules
  featureRequested?: Features
  title?: string
  description?: string
}

export type GetUpsellingDataReturns = {
  moduleName?: string | null
  moduleDescription?: string | null
  upsellModalTitle?: string | null
  upsellModalDescription?: string | null
  upsellModalImage?: string | null
  upsellModalBenefits: string[]
  moduleSlug?: string | null
  loading: boolean
  moduleAvailable: boolean
  featureAvailable: boolean
}

export const useGetUpsellingData = ({
  moduleRequested,
  featureRequested,
  title,
  description,
}: Props): GetUpsellingDataReturns => {
  const {
    company,
    access: { locale: user_locale },
  } = useCurrentSession()
  const mainCountry = company.legalEntities.find((legalEntity) => legalEntity.main)?.country
  const datoFormatLocale = locale[user_locale]

  const { data: datoModules, loading: dataLoading } = useGetDatoModules({
    chargebeeModuleName: moduleRequested,
    countryCode: countryCode(mainCountry || ''),
    locale: datoFormatLocale,
  })

  const { featureAvailability, loading: featureLoading } = useFeatureAvailability(
    featureRequested || '',
    company.id
  )

  const moduleName = title || datoModules.at(0)?.name
  const moduleDescription = description || datoModules.at(0)?.shortDescription

  const upsellModalTitle = datoModules.at(0)?.upsellTitle
  const upsellModalDescription = datoModules.at(0)?.upsellDescription
  const upsellModalImage = datoModules.at(0)?.upsellImage?.responsiveImage?.src
    ? `${datoModules.at(0)?.upsellImage?.responsiveImage?.src}?w=335&h=362&fit=scale`
    : undefined
  const upsellModalPerks = datoModules.at(0)?.upsellPerks || ''
  const moduleSlug = datoModules.at(0)?.slug

  const upsellModalBenefits = upsellModalPerks
    .split('.')
    .slice(0, -1)
    .map((perk) => perk.trim())

  const loading = dataLoading || (featureRequested ? featureLoading : false)
  const featureAvailable = featureRequested ? featureAvailability.available : true

  return {
    moduleName,
    moduleDescription,
    upsellModalTitle,
    upsellModalDescription,
    upsellModalImage,
    upsellModalBenefits,
    moduleSlug,
    loading,
    moduleAvailable: datoModules.length > 0,
    featureAvailable,
  }
}
